import React, { useEffect, useState } from "react";
import {
  locationsFormMapDispatchToProps,
  locationsFormMapStateToProps,
  locationsFormReducer,
} from "./LocationsForm.state";
import { useHistory, useRouteMatch } from "react-router-dom";
import {
  LocationResponse,
  ErrorWithMessage,
  FormType,
  ReduxFreeformActions,
  ReduxStoreRoot,
  MatchOnRouteId,
  Pages,
} from "../../types";
import { useClearFormOnUnmount, useSetFormFieldsState } from "../../hooks";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery } from "@tanstack/react-query";
import { fetchFunction, getErrorDictionary } from "../../util";
import {
  ButtonWithAction,
  Cluster,
  FormInput,
  FormSelect,
  Loading,
  Title,
} from "@thecb/components";
import { FormContainer, Navigation } from "../..";
import { ALERTS, CREATE } from "../../util/constants";
import { RequestMethod } from "../../types/api/RequestMethod";
import { replacePOSAlerts } from "../../state/root";
import { AlertVariants } from "../../types/Alert";
import { LocationDataFields } from "../../types/api/pos-api/location-api";

interface Props {
  actions: {
    forms: {
      locationsForm: {
        actions: ReduxFreeformActions;
      };
    };
  };
}

const LocationsForm = ({ actions }: Props) => {
  const [formType, setFormType] = useState<FormType>(FormType.EDIT);
  const history = useHistory();
  const dispatch = useDispatch();
  const formActions = actions.forms.locationsForm.actions;
  useClearFormOnUnmount(formActions.form);
  const { locationsForm } = useSelector(
    (state: ReduxStoreRoot) => state.posConfigTool.locations.forms
  );
  const {
    client: {
      value: { brand: selectedBrand },
    },
  } = useSelector((state: ReduxStoreRoot) => state.currentUser);
  const { posApiEndpoint } = useSelector((state: ReduxStoreRoot) => state.config);
  const match: MatchOnRouteId | null = useRouteMatch("/pos-config/locations/:id");
  const locationId = match?.params?.id;

  const { data: editLocationsData, isFetching: isFetchingView } = useQuery<
    LocationResponse,
    ErrorWithMessage
  >({
    queryKey: [`GET -> /locations/${locationId}`],
    queryFn: () => fetchFunction(`${posApiEndpoint}/locations/${locationId}/edit`),
    enabled: locationId !== FormType.CREATE,
  });

  const { data: createLocationData, isFetching: isFetchingCreate } = useQuery<
    LocationResponse,
    ErrorWithMessage
  >({
    queryKey: [`GET -> /locations/${selectedBrand}/new`],
    queryFn: () =>
      fetchFunction(`${posApiEndpoint}/locations/new?brand=${selectedBrand}`),
    enabled: formType === FormType.CREATE,
  });

  const { mutate: createLocation, isLoading: isLoadingPOST } = useMutation<
    LocationResponse,
    ErrorWithMessage,
    { location: Partial<LocationDataFields> }
  >({
    mutationFn: (body: { location: Partial<LocationDataFields> }) =>
      fetchFunction(`${posApiEndpoint}/locations`, {
        method: RequestMethod.POST,
        body,
      }),
    onSuccess: (res: LocationResponse) => {
      const newLocationLink = history.location.pathname.replace(
        CREATE,
        `${res.data.id}/edit`
      );
      history.push(newLocationLink);
      setFormType(FormType.EDIT);
      dispatch(
        replacePOSAlerts({
          variant: AlertVariants.SUCCESS,
          heading: ALERTS.SUCCESS,
          text: `${res.data.name} Successfully Created`,
          alertClearTimeout: 3000,
        })
      );
    },
  });

  useSetFormFieldsState(formActions, editLocationsData);
  useSetFormFieldsState(formActions, createLocationData);

  useEffect(() => {
    if (locationId === FormType.CREATE) {
      setFormType(FormType.CREATE);
    }
  }, [locationId]);

  const {
    mutate: updateLocation,
    isLoading: isLoadingPUT,
    data: updateLocationResponse,
  } = useMutation<
    LocationResponse,
    ErrorWithMessage,
    { location: Partial<LocationDataFields> }
  >({
    mutationFn: (body: { location: Partial<LocationDataFields> }) =>
      fetchFunction(`${posApiEndpoint}/locations/${body.location.id}`, {
        method: RequestMethod.PUT,
        body,
      }),
    onSuccess: (res: LocationResponse) =>
      dispatch(
        replacePOSAlerts({
          variant: AlertVariants.SUCCESS,
          heading: ALERTS.SUCCESS,
          text: `${res.data.name} Successfully Updated`,
          alertClearTimeout: 3000,
        })
      ),
  });

  useSetFormFieldsState(formActions, updateLocationResponse);

  const statesData = editLocationsData ??
    createLocationData ?? { data: { states: [""] } };
  const states = statesData.data.states.map((state) => ({
    text: state,
    value: state,
    id: state,
  }));
  if (editLocationsData || formType === FormType.CREATE) {
    return (
      <>
        <Title as="h1" weight="bold">
          Locations
        </Title>
        <Navigation page={Pages.LOCATIONS} />
        {formType === FormType.CREATE ? (
          <Title as="h1" weight="bold" extraStyles={`margin: 0.5rem 0`}>
            Create Location
          </Title>
        ) : (
          ""
        )}
        <FormContainer header={editLocationsData?.data?.name ?? ""}>
          <Title
            as="h2"
            variant="small"
            weight="600"
            extraStyles={`margin-bottom: 0.5rem`}
          >
            Settings and Information
          </Title>
          <FormInput
            errorMessages={getErrorDictionary("Name")}
            fieldActions={formActions.fields.name}
            field={locationsForm.name}
            labelTextWhenNoError="Name"
          />
          <FormInput
            errorMessages={getErrorDictionary("Location code")}
            fieldActions={formActions.fields.location_code}
            field={locationsForm.location_code}
            labelTextWhenNoError="Location code"
          />
          <FormInput
            errorMessages={getErrorDictionary("Email address")}
            fieldActions={formActions.fields.email}
            field={locationsForm.email}
            labelTextWhenNoError="Email address"
          />
          <FormInput
            errorMessages={getErrorDictionary("Phone number")}
            fieldActions={formActions.fields.phone_1}
            field={locationsForm.phone_1}
            labelTextWhenNoError="Primary phone number"
          />
          <FormInput
            errorMessages={getErrorDictionary("Phone Number Secondary")}
            fieldActions={formActions.fields.phone_2}
            field={locationsForm.phone_2}
            labelTextWhenNoError="Secondary phone number (Optional)"
          />
          <Title as="h2" variant="small" weight="600" margin="16px 0px 0px">
            Address
          </Title>
          <FormInput
            errorMessages={getErrorDictionary("Address")}
            fieldActions={formActions.fields.address_1}
            field={locationsForm.address_1}
            labelTextWhenNoError="Address"
          />
          <FormInput
            errorMessages={getErrorDictionary("Address secondary")}
            fieldActions={formActions.fields.address_2}
            field={locationsForm.address_2}
            labelTextWhenNoError="Apt, Suite, Unit, Floor, etc. (Optional)"
          />
          <FormInput
            errorMessages={getErrorDictionary("City")}
            fieldActions={formActions.fields.city}
            field={locationsForm.city}
            labelTextWhenNoError="City"
          />
          <FormSelect
            labelTextWhenNoError="State"
            errorMessages={getErrorDictionary("state")}
            options={states}
            field={locationsForm.state}
            fieldActions={formActions.fields.state}
            showErrors={false}
          />
          <FormInput
            errorMessages={getErrorDictionary("zip code")}
            fieldActions={formActions.fields.zipcode}
            field={locationsForm.zipcode}
            labelTextWhenNoError="Zip code"
          />
          <Title as="h2" variant="small" weight="600" margin="16px 0px 0px">
            Location Config
          </Title>
          <FormInput
            errorMessages={getErrorDictionary("default_float_amount")}
            fieldActions={formActions.fields.default_float}
            field={locationsForm.default_float}
            labelTextWhenNoError="Default float amount"
          />
          <Cluster justify="flex-end">
            <ButtonWithAction
              disabled={Object.values(locationsForm).some(
                (formValue) => formValue.hasErrors
              )}
              extraStyles="margin: 0;"
              text={
                formType === FormType.CREATE ? "Create Location" : "Save Changes"
              }
              action={(e) => {
                e.preventDefault();
                if (formType === FormType.EDIT && editLocationsData) {
                  updateLocation({
                    location: {
                      id: editLocationsData.data.id,
                      name: locationsForm.name.rawValue ?? "",
                      location_code: locationsForm.location_code.rawValue ?? "",
                      state: locationsForm.state.rawValue ?? "",
                      city: locationsForm.city.rawValue ?? "",
                      zipcode: locationsForm.zipcode.rawValue ?? "",
                      email: locationsForm.email.rawValue ?? "",
                      phone_1: locationsForm.phone_1.rawValue ?? "",
                      phone_2: locationsForm.phone_2.rawValue ?? "",
                      address_1: locationsForm.address_1.rawValue ?? "",
                      address_2: locationsForm.address_2.rawValue ?? "",
                      locations_config_attributes: {
                        default_float: locationsForm.default_float.rawValue ?? "",
                      },
                    },
                  });
                }
                if (formType === FormType.CREATE && createLocationData) {
                  createLocation({
                    location: {
                      name: locationsForm.name.rawValue ?? "",
                      client_id: createLocationData.data.client_id,
                      location_code: locationsForm.location_code.rawValue ?? "",
                      state: locationsForm.state.rawValue ?? "",
                      city: locationsForm.city.rawValue ?? "",
                      zipcode: locationsForm.zipcode.rawValue ?? "",
                      email: locationsForm.email.rawValue ?? "",
                      phone_1: locationsForm.phone_1.rawValue ?? "",
                      phone_2: locationsForm.phone_2.rawValue ?? "",
                      address_1: locationsForm.address_1.rawValue ?? "",
                      address_2: locationsForm.address_2.rawValue ?? "",
                      locations_config_attributes: {
                        default_float: locationsForm.default_float.rawValue ?? "",
                      },
                    },
                  });
                }
              }}
            />
          </Cluster>
        </FormContainer>
      </>
    );
  }

  if (isFetchingView || isLoadingPUT || isFetchingCreate || isLoadingPOST) {
    return <Loading />;
  }

  return null;
};

LocationsForm.reducer = locationsFormReducer;
LocationsForm.mapStateToProps = locationsFormMapStateToProps;
LocationsForm.mapDispatchToProps = locationsFormMapDispatchToProps;

export default LocationsForm;
